import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "@/lib/auth";
import { CompanyProvider } from "@/lib/company";
import { ThemeProvider } from "@/lib/theme";
import Layout from "@/components/Layout";
import ProtectedRoute from "@/components/ProtectedRoute";
import Landing from "@/pages/Landing";
import AuthPage from "@/pages/AuthPage";
import Dashboard from "@/pages/Dashboard";
import Invoices from "@/pages/Invoices";
import InvoiceCreate from "@/pages/InvoiceCreate";
import InvoiceDetail from "@/pages/InvoiceDetail";
import CreditNotes from "@/pages/CreditNotes";
import Items from "@/pages/Items";
import Parties from "@/pages/Parties";
import Expenses from "@/pages/Expenses";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import CompanySetup from "@/pages/CompanySetup";
import Ledgers from "@/pages/Ledgers";
import { VoucherList, VoucherCreate } from "@/pages/Vouchers";
import TallyReports from "@/pages/TallyReports";
import JobCards from "@/pages/JobCards";
import RecurringVouchers from "@/pages/RecurringVouchers";
import { Quotations, SalesOrders } from "@/pages/SalesCycle";
import StockSummary from "@/pages/StockSummary";
import FixedAssets from "@/pages/FixedAssets";
import { PurchaseRequests, PurchaseOrders, GRNs } from "@/pages/PurchaseCycle";
import AcceptInvite from "@/pages/AcceptInvite";
import SuperAdminConsole from "@/pages/SuperAdminConsole";
import Contact from "@/pages/Contact";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";

function TitleGuard() {
  const location = useLocation();
  useEffect(() => { document.title = "SERP"; }, [location.pathname]);
  return null;
}

function LandingOrDashboard() {
  const { user, ready } = useAuth();
  if (!ready) return null;
  if (user) return <Navigate to="/app/dashboard" replace />;
  return <Landing />;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <CompanyProvider>
          <BrowserRouter>
            <TitleGuard />
            <Toaster
              position="bottom-right"
              toastOptions={{
                style: {
                  background: "#1C1C1C",
                  border: "1px solid #1C1C1C",
                  color: "#FFFFFF",
                  borderRadius: "6px",
                  fontSize: 13,
                },
              }}
            />
            <Routes>
              <Route path="/" element={<LandingOrDashboard />} />
              <Route path="/login" element={<AuthPage mode="login" />} />
              <Route path="/register" element={<AuthPage mode="register" />} />
              <Route path="/accept-invite" element={<AcceptInvite />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />

              <Route path="/app" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
                <Route index element={<Navigate to="/app/dashboard" replace />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="invoices" element={<Invoices />} />
                <Route path="quotations" element={<Quotations />} />
                <Route path="sales-orders" element={<SalesOrders />} />
                <Route path="purchase-requests" element={<PurchaseRequests />} />
                <Route path="purchase-orders" element={<PurchaseOrders />} />
                <Route path="grns" element={<GRNs />} />
                <Route path="stock-summary" element={<StockSummary />} />
                <Route path="fixed-assets" element={<FixedAssets />} />
                <Route path="invoices/new" element={<InvoiceCreate />} />
                <Route path="invoices/:id" element={<InvoiceDetail />} />
                <Route path="invoices/:id/edit" element={<InvoiceCreate />} />
                <Route path="credit-notes" element={<CreditNotes />} />
                <Route path="purchases" element={<Invoices direction="purchase" />} />
                <Route path="purchases/new" element={<InvoiceCreate direction="purchase" />} />
                <Route path="ledgers" element={<Ledgers />} />
                <Route path="vouchers" element={<VoucherList />} />
                <Route path="vouchers/new" element={<VoucherCreate />} />
                <Route path="books" element={<TallyReports />} />
                <Route path="job-cards" element={<JobCards />} />
                <Route path="recurring-vouchers" element={<RecurringVouchers />} />
                <Route path="items" element={<Items />} />
                <Route path="parties" element={<Parties />} />
                <Route path="expenses" element={<Expenses />} />
                <Route path="reports" element={<Reports />} />
                <Route path="settings" element={<Settings />} />
                <Route path="setup" element={<CompanySetup />} />
                <Route path="admin" element={<SuperAdminConsole />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </CompanyProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
